import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import Icon from './Icon';

const CreditBalance = forwardRef(({ token, onCreditsEmpty = null, onClick = null }, ref) => {
  const [credits, setCredits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCreditBalance = async () => {
    if (!token) {
      setCredits(null);
      setLoading(false);
      return;
    }

    try {
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${API_BASE}/api/v1/credits/balance`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCredits(data.current_balance);
        setError(null);
        
        // Callback when credits are empty
        if (data.current_balance === 0 && onCreditsEmpty) {
          onCreditsEmpty();
        }
      } else if (response.status === 401) {
        setError('Session expired');
      } else {
        setError('Failed to load credits');
      }
    } catch (err) {
      console.error('Error fetching credits:', err);
      setError('Network error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCreditBalance();
    // Refresh credits every 30 seconds
    const interval = setInterval(fetchCreditBalance, 30000);
    return () => clearInterval(interval);
  }, [token]);

  // Expose refresh function to parent component
  useImperativeHandle(ref, () => ({
    refreshCredits: fetchCreditBalance,
    getCurrentCredits: () => credits
  }));

  // Don't show credit balance for therapists (they don't use credits)
  const user = token ? JSON.parse(atob(token.split('.')[1])) : null;
  if (user && user.role === 'therapist') {
    return null;
  }

  if (loading) {
    return (
      <div className="flex items-center space-x-2 bg-blue-50 rounded-md px-3 py-1.5">
        <Icon name="loader" size={14} className="text-blue-500 animate-spin" />
        <span className="text-sm text-blue-600">Laddar...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center space-x-2 bg-red-50 rounded-md px-3 py-1.5">
        <Icon name="alert-circle" size={14} className="text-red-500" />
        <span className="text-sm text-red-600">Fel</span>
      </div>
    );
  }

  if (credits === null) {
    return null;
  }

  // Only show credit balance when credits are less than 5
  if (credits >= 5) {
    return null;
  }

  // Color scheme based on credit level
  const getColorScheme = () => {
    if (credits === 0) {
      return {
        bg: 'bg-red-50',
        text: 'text-red-600',
        icon: 'text-red-500'
      };
    } else if (credits <= 5) {
      return {
        bg: 'bg-yellow-50',
        text: 'text-yellow-700',
        icon: 'text-yellow-500'
      };
    } else {
      return {
        bg: 'bg-emerald-50',
        text: 'text-emerald-700',
        icon: 'text-emerald-500'
      };
    }
  };

  const colorScheme = getColorScheme();

  const handleClick = () => {
    if (onClick) {
      onClick(credits);
    }
  };

  return (
    <div 
      className={`flex items-center space-x-2 ${colorScheme.bg} rounded-md px-3 py-1.5 cursor-pointer hover:opacity-80 transition-opacity`} 
      title={`Du har ${credits} credits kvar${credits <= 5 ? '. Klicka för att fylla på.' : ''}`}
      onClick={handleClick}
    >
      <Icon 
        name={credits === 0 ? "alert-triangle" : "credit-card"} 
        size={14} 
        className={colorScheme.icon} 
      />
      <span className={`text-sm font-medium ${colorScheme.text}`}>
        {credits} credit{credits !== 1 ? 's' : ''}
      </span>
      {credits <= 5 && credits > 0 && (
        <Icon name="warning" size={12} className="text-yellow-500" />
      )}
    </div>
  );
});

export default CreditBalance;
